import React, { useEffect, useState } from "react";
import {
  useMediaQuery,
  Box,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import AdminSidebar from "./AdminSidebar";
import { Search } from "@mui/icons-material";
import StudentStatistic from "../../components/cards/StudentStatistic";
import color from "../../components/utils/Colors";
import FeedbackCard from "../../components/cards/FeedbackCard";
import { SkillPopularityCard } from "../../components/cards/SkillPopularityCard";
import CourseVisibilityCard from "../../components/cards/CourseVisibilityCard";
import TeacherRatingCard from "../../components/cards/TeacherRatingCard";
import UnverifiedCourseCard from "../../components/cards/UnverifiedCourseCard";
import { fetchAdminHomeData, shiftMonth } from "../../services/adminData";
import { useUser } from "../../services/Context/UserContext";

const AdminHome = () => {
  const isSmallScreen = useMediaQuery("(max-width:1050px)");
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [dashboardData, setDashboardData] = useState<Awaited<
    ReturnType<typeof fetchAdminHomeData>
  > | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchAdminHomeData(selectedMonth)
      .then(setDashboardData)
      .finally(() => setLoading(false));
  }, [selectedMonth]);

  const handlePeriodChange = (direction: "previous" | "next") => {
    setSelectedMonth((current) =>
      shiftMonth(current, direction === "next" ? "next" : "previous"),
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        backgroundImage: "url('/images/logo 2.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: "auto",
        margin: isSmallScreen ? "10px" : "30px",
        marginTop: "0px",
        paddingTop: "20px",
        gap: "3%",
        borderRadius: "10px",
        flexDirection: isSmallScreen ? "column" : "row",
      }}
    >
      <AdminSidebar />

      <Box sx={{ flex: 1, borderRadius: "16px" }}>
        <TextField
          className="input-root2"
          placeholder="Search..."
          size="small"
          InputProps={{
            startAdornment: (
              <IconButton>
                <Search />
              </IconButton>
            ),
          }}
          sx={{ width: { xs: "100%", md: "auto" } }}
        />

        <Box
          sx={{
            position: "relative",
            background: color.firstColor,
            borderRadius: "16px",
            height: "fit-content",
            my: 4,
          }}
        >
          <Box
            component="img"
            src="/assets/Welcome Bar.svg"
            alt="Welcome Bar"
            sx={{ width: "100%", height: 200, objectFit: "contain" }}
          />
          <Typography
            sx={{
              color: "white",
              position: "absolute",
              top: { xs: "15%", sm: "25%" },
              left: "3%",
              fontSize: "22px",
              fontWeight: "bold",
            }}
          >
            Welcome back, {user?.name || "Admin"}
          </Typography>
          <Typography
            sx={{
              color: "white",
              position: "absolute",
              left: "3%",
              maxWidth: "62%",
              top: { xs: "34%", sm: "50%" },
            }}
          >
            You have {dashboardData?.newStudentsCount ?? 0} new student
            {dashboardData?.newStudentsCount === 1 ? "" : "s"} added to your
            domain in the last 30 days. Monthly revenue:{" "}
            {dashboardData?.totalRevenue?.toLocaleString() ?? 0}.
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: color.firstColor }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <StudentStatistic
              data={dashboardData?.studentData ?? []}
              periodLabel={dashboardData?.periodLabel ?? ""}
              onPeriodChange={handlePeriodChange}
            />
            <SkillPopularityCard skills={dashboardData?.skillsData ?? []} />
            <FeedbackCard items={dashboardData?.feedbackItems ?? []} />
            <CourseVisibilityCard
              data={dashboardData?.courseData ?? []}
              periodLabel={dashboardData?.periodLabel ?? ""}
              onPeriodChange={handlePeriodChange}
            />
            <TeacherRatingCard ratings={dashboardData?.ratings ?? []} />
            <UnverifiedCourseCard courses={dashboardData?.unverifiedCourses ?? []} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminHome;
